import { useEffect, useState } from 'react';
import type { ReservationSpanPaymentLinkFilter } from '@repo/domain';
import { BusinessValidationCode, BusinessValidationError, useReservationSpanPaymentLink } from '@repo/domain-service';
import { tr } from '@repo/locale';
import { TestId } from '@repo/test-id';
import { generatePath, useNavigate, useParams } from 'react-router-dom';

import { RESERVATION_SPANS_ANCHOR } from '@features/account/constants/reservation';
import { AuthModal, selectStatus, useAuthStore } from '@features/auth';
import PaymentBlikWaitContents from '@features/reservation/components/payment/PaymentBlikWaitContents';
import SpanPaymentLinkInvalidContents from '@features/reservation/components/spanPaymentLink/SpanPaymentLinkInvalidContents';
import SpanPaymentLinkMethodsContents from '@features/reservation/components/spanPaymentLink/SpanPaymentLinkMethodsContents';
import SpanPaymentLinkStartContents from '@features/reservation/components/spanPaymentLink/SpanPaymentLinkStartContents';
import { useSpanPaymentLinkConfirmation } from '@features/reservation/hooks/useSpanPaymentLinkConfirmation';
import { useSpanPaymentLinkLogin } from '@features/reservation/hooks/useSpanPaymentLinkLogin';
import { readSpanPaymentLinkSignature } from '@features/reservation/utils/spanPaymentLinkSignature';

import PleaseWaitAnimation from '@shared/components/PleaseWaitAnimation';

import { ROUTE_PATHS } from '@router/paths';
import { RouteName } from '@router/routeNames';

type SpanPaymentLinkView = 'start' | 'methods' | 'blikWait' | 'invalid';

const testId = TestId.root('reservation.spanPaymentLink');

/**
 * `/reservation/span-payment/:reservationSpanId?signature=…` — the payment link from the reminder email (the signature
 * is lifted out of the URL on arrival, see `captureSpanPaymentLinkSignatureFromUrl`). The renter
 * signed in may pay for the span or open the reservation, and is led to it once the span is paid; anybody else may pay
 * for the span without logging in, or log in.
 */
export default function SpanPaymentLinkPage() {
  const navigate = useNavigate();
  const { reservationSpanId = '' } = useParams();
  const filter: ReservationSpanPaymentLinkFilter = {
    reservationSpanId,
    signature: readSpanPaymentLinkSignature(reservationSpanId),
  };
  const isAuthenticated = useAuthStore(selectStatus) === 'authenticated';
  const {
    reservationSpanPaymentLink,
    loadingReservationSpanPaymentLink,
    reservationSpanPaymentLinkError,
    refetchReservationSpanPaymentLink,
  } = useReservationSpanPaymentLink(filter);
  const [view, setView] = useState<SpanPaymentLinkView>('start');

  const isLinkRefused =
    !!reservationSpanPaymentLinkError ||
    (!loadingReservationSpanPaymentLink && !reservationSpanPaymentLink) ||
    reservationSpanPaymentLink?.status === 'paid';
  const reservationId = reservationSpanPaymentLink?.reservationId;
  const isRenter = isAuthenticated && !!reservationId;
  const isPaidForRenter = isRenter && reservationSpanPaymentLink?.status === 'paid';

  useEffect(() => {
    if (!isPaidForRenter || !reservationId) return;
    navigate(generatePath(ROUTE_PATHS[RouteName.ACCOUNT_RESERVATION], { id: reservationId }), { replace: true });
  }, [isPaidForRenter, reservationId, navigate]);

  useSpanPaymentLinkConfirmation({
    filter,
    awaiting: view === 'blikWait',
    onPaid: () => navigate(ROUTE_PATHS[RouteName.RESERVATION_SPAN_PAYMENT_LINK_SUCCESS], { replace: true }),
    onFailed: () => navigate(ROUTE_PATHS[RouteName.RESERVATION_SPAN_PAYMENT_LINK_FAILURE], { replace: true }),
  });

  // Back from the login, the link is asked again: the renter then gets RESERVATION DETAILS.
  const { hasSession, showLogin, requestLogin, handleLoginOpenChange } = useSpanPaymentLinkLogin(() => {
    void refetchReservationSpanPaymentLink();
  });
  const loginDrawer = showLogin ? <AuthModal onOpenChange={handleLoginOpenChange} /> : undefined;
  const loginButtonLabel = tr(
    hasSession ? 'reservations:span_payment_link.log_in_to_another_account' : 'reservations:span_payment_link.log_in'
  );

  const handleLogin = () => {
    requestLogin();
  };

  const handlePayNow = () => {
    setView('methods');
  };

  const handleBackToStart = () => {
    setView('start');
  };

  const handleBlikPaymentInitiated = () => {
    setView('blikWait');
  };

  const handleLinkInvalid = () => {
    setView('invalid');
  };

  const handleShowReservation = () => {
    if (!reservationId) return;
    navigate({
      pathname: generatePath(ROUTE_PATHS[RouteName.ACCOUNT_RESERVATION], { id: reservationId }),
      hash: RESERVATION_SPANS_ANCHOR,
    });
  };

  if (loadingReservationSpanPaymentLink) return <PleaseWaitAnimation testId={testId} />;

  // The renter of a span already paid is on their way to the reservation.
  if (isPaidForRenter) return <PleaseWaitAnimation testId={testId} />;

  if (reservationSpanPaymentLinkError && !isLinkRefusal(reservationSpanPaymentLinkError)) {
    throw reservationSpanPaymentLinkError;
  }

  if (view === 'invalid' || isLinkRefused || !reservationSpanPaymentLink) {
    return (
      <SpanPaymentLinkInvalidContents
        testId={testId.append('invalid')}
        loginButtonLabel={loginButtonLabel}
        drawer={loginDrawer}
        onLogin={handleLogin}
      />
    );
  }

  switch (view) {
    case 'start':
      return (
        <SpanPaymentLinkStartContents
          testId={testId.append('start')}
          link={reservationSpanPaymentLink}
          isRenter={isRenter}
          loginButtonLabel={loginButtonLabel}
          drawer={loginDrawer}
          onPayNow={handlePayNow}
          onLogin={handleLogin}
          onShowReservation={handleShowReservation}
        />
      );
    case 'methods':
      return (
        <SpanPaymentLinkMethodsContents
          testId={testId.append('methods')}
          filter={filter}
          price={reservationSpanPaymentLink.price}
          onGoBack={handleBackToStart}
          onBlikPaymentInitiated={handleBlikPaymentInitiated}
          onLinkInvalid={handleLinkInvalid}
        />
      );
    case 'blikWait':
      return <PaymentBlikWaitContents onGoBack={handleBackToStart} />;
    default: {
      const exhaustiveCheck: never = view;
      return exhaustiveCheck;
    }
  }
}

/** The backend refuses every unusable link with one code; any other failure is an error of the page. */
function isLinkRefusal(error: Error): boolean {
  return (
    error instanceof BusinessValidationError && error.code === BusinessValidationCode.reservationSpanPaymentLinkInvalid
  );
}
